import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useAuth } from "../msalAuth/AuthProvider";
import { useEnv } from "./utils";
import collapse from "../../assets/chat/collapse-white-32.png";
import { consumePendingCampaign } from "../../config/campaigns";

export default function AIChatbox() {
  const { language } = useContext(LanguageContext);
  const { user } = useAuth();
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_AGENT_ID"]);

  const dfMessengerRef = useRef(null);
  const parameterRef = useRef(false);
  const languageRef = useRef(language || "th");
  // const autoDetectLanguageRef = useRef(true);
  const isInitializedRef = useRef(false);
  const pendingAskRef = useRef(null);
  const DF_DEBUG = false;

  const dfLog = (label, ...args) => {
    if (DF_DEBUG) console.log(`[DF] ${label}`, ...args);
  };

  const META_KEYS = [
    ["df_business_unit", "business_unit"],
    ["df_office_location", "office_location"],
    ["df_computer_model", "computer_model"],
    ["df_sub_business_unit", "sub_business_unit"],
    ["df_employment_type", "employment_type"],
    ["df_company", "company"],
    ["df_o365_licenses", "o365_licenses", "json"],
    ["df_access_groups", "access_groups", "json"],
    ["df_start_time", "start_time", "json"],
  ];
  const serialize = (value, kind) => (kind === "json" ? JSON.stringify(value) : String(value));

  const snapshotStorage = () => Object.fromEntries(META_KEYS.map(([k]) => [k, sessionStorage.getItem(k)]));

  const clearMetadata = (reason = "unknown") => {
    const before = snapshotStorage();
    META_KEYS.forEach(([storageKey]) => sessionStorage.removeItem(storageKey));
    dfLog(`CLEAR (${reason}) — wiped:`, JSON.stringify(before));
  };

  const syncMetadata = (sessionParams) => {
    if (!sessionParams || typeof sessionParams !== "object") {
      return;
    }

    const changes = [];

    META_KEYS.forEach(([storageKey, paramKey, kind]) => {
      const previous = sessionStorage.getItem(storageKey);
      const value = sessionParams[paramKey];

      const isEmpty =
        value === undefined || value === null || value === "" || (kind === "json" && typeof value === "object" && Object.keys(value).length === 0);

      if (isEmpty) {
        if (previous !== null) {
          sessionStorage.removeItem(storageKey);
          changes.push(`REMOVED ${paramKey}`);
        }
        return;
      }

      const next = serialize(value, kind);
      if (previous !== next) {
        sessionStorage.setItem(storageKey, next);
        changes.push(previous === null ? `ADDED ${paramKey}` : `UPDATED ${paramKey}`);
      }
    });

    if (changes.length) dfLog("SYNC changes:", changes);
    else dfLog("SYNC — no changes");

    dfLog("SYNC storage now:", JSON.stringify(snapshotStorage()));
  };

  const readMetadata = () =>
    Object.fromEntries(
      META_KEYS.map(([storageKey, paramKey, kind]) => {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return null;
        if (kind !== "json") return [paramKey, raw];
        try {
          return [paramKey, JSON.parse(raw)];
        } catch {
          dfLog(`readMetadata — bad JSON for ${storageKey}, dropping`);
          return null;
        }
      }).filter(Boolean)
    );

  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "greeting" },
      },
    ],
    [language]
  );

  const attachEmail = useCallback(() => {
    const email = user?.username;
    if (!email || !dfMessengerRef.current) return;

    dfMessengerRef.current.setQueryParameters({
      parameters: { email: email },
    });
    parameterRef.current = true;
  }, [user?.username]);

  const initializeChat = useCallback(
    (messenger) => {
      if (!messenger || isInitializedRef.current) return;

      dfLog("INIT start");
      clearMetadata("initializeChat");
      messenger.startNewSession({ retainHistory: false });

      setTimeout(() => {
        attachEmail();
        messenger.sendRequest("event", "greeting");
        isInitializedRef.current = true;
      }, 500);
    },
    [attachEmail]
  );

  const clearQueryParameters = useCallback(() => {
    if (parameterRef.current && dfMessengerRef.current) {
      dfMessengerRef.current.setQueryParameters({});
      parameterRef.current = false;
    }
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }

    const requestBody = event.detail?.data?.requestBody;
    if (!requestBody) {
      dfLog("SENT — no requestBody, aborting");
      return;
    }

    if (requestBody.queryInput) {
      requestBody.queryInput.languageCode = languageRef.current;
      dfLog("SENT languageCode:", languageRef.current);
    }
    const endUserMetadata = readMetadata();
    if (Object.keys(endUserMetadata).length > 0) {
      requestBody.queryParams = { ...requestBody.queryParams, endUserMetadata };
      dfLog("SENT metadata:", JSON.stringify(endUserMetadata));
    } else {
      dfLog("SENT metadata: (none — storage empty)");
    }

    dfLog("SENT queryInput:", JSON.stringify(requestBody.queryInput));
  }, []);

  // const handleSentRequest = (event) => {
  //   const input = event.detail.data.requestBody.queryInput;
  //   let nextLanguageCode = currentLanguageRef.current;

  //   if ("text" in input) {
  //     const text = input.text.text;
  //     if (autoDetectLanguageRef.current) {
  //       const langCode = detectLanguage(text);
  //       input.languageCode = langCode;
  //       nextLanguageCode = langCode;
  //     } else {
  //       input.languageCode = currentLanguageRef.current;
  //     }
  //   } else {
  //     input.languageCode = currentLanguageRef.current;
  //   }

  //   if (nextLanguageCode !== currentLanguageRef.current) {
  //     currentLanguageRef.current = nextLanguageCode;
  //   }
  // };

  const handleChatSession = useCallback(
    (event) => {
      if (!event.detail.message || !dfMessengerRef.current) return;

      isInitializedRef.current = false;
      initializeChat(dfMessengerRef.current);
    },
    [initializeChat]
  );

  const handleSessionExpired = useCallback(() => {
    // if (!dfMessengerRef.current) return;

    if (!dfMessengerRef.current) {
      return;
    }

    clearMetadata("sessionExpired");

    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const sendQuery = useCallback((text) => {
    const messenger = dfMessengerRef.current;
    if (!messenger || !text) return;
    // Bubble first, then send — keep that order. Google documents sendRequest as waiting for
    // the response, so drawing the bubble afterwards puts the user's own question UNDER the
    // answer to it.
    messenger.renderCustomText(text, false);
    messenger.sendRequest("query", text);
  }, []);

  const sendPendingAsk = useCallback(() => {
    const text = pendingAskRef.current;
    if (!text || !dfMessengerRef.current) return;
    pendingAskRef.current = null;
    setTimeout(() => sendQuery(text), 300);
  }, [sendQuery]);

  const handleClickButton = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;
      const messenger = dfMessengerRef.current;

      const { event: evt, text } = event.detail;
      if (evt) {
        messenger.renderCustomText(text, false);
        if (evt === "greeting") {
          attachEmail();
          return;
        } else {
          clearQueryParameters();
          return;
        }
      }
      clearQueryParameters();
      if (text) {
        messenger.renderCustomText(text, false);
        messenger.sendRequest("query", text);
      } else {
        messenger.sendRequest("event", "flow-failed");
      }
    },
    [clearQueryParameters, attachEmail]
  );

  const renderTicket = useCallback((ticket, index) => {
    if (!dfMessengerRef.current) return;

    let { number, state, short_description, opened_date } = ticket;
    if (short_description === "-") {
      short_description = "Undefined";
    }

    const btn = [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: `${index + 1}) ${number} - ${opened_date} - ${state} - ${short_description}`,
      },
    ];

    dfMessengerRef.current.renderCustomCard(btn);
  }, []);

  const handleResponse = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;

      const sessionParams = event.detail?.raw?.queryResult?.parameters;
      // dfLog("RECV meta subset:", JSON.stringify(Object.fromEntries(META_KEYS.map(([, p]) => [p, sessionParams?.[p]]))));
      syncMetadata(sessionParams);

      const messages = event.detail.data.messages;
      // dfLog(
      //   `RECV ${messages?.length ?? 0} message(s):`,
      //   messages?.map((m) => m.type)
      // );

      for (const message of messages) {
        if (message.type !== "text") continue;

        const actionRegex = /"action"\s*:\s*"([^"]+)"/;
        const match = message.text.match(actionRegex);

        if (!match || match[1] !== "followup_list_tickets") continue;

        event.preventDefault();

        try {
          const obj = JSON.parse(message.text);
          if (!obj.tickets || !obj.ticket_stat) continue;

          obj.tickets.forEach(renderTicket);
          dfMessengerRef.current.renderCustomText(obj.ticket_stat, true);
        } catch (error) {
          console.error("Error parsing ticket data:", error);
        }

        // autoDetectLanguageRef.current = true;
        // const currentPage = event.detail?.raw?.queryResult?.currentPage?.name || "";
        // if (currentPage) {
        //   const parts = currentPage.split("/");
        //   let currentFlowID = "";
        //   let currentPageID = "";

        //   for (let i = 0; i < parts.length; i++) {
        //     if (parts[i] === "flows" && i + 1 < parts.length) {
        //       currentFlowID = parts[i + 1];
        //     }
        //     if (parts[i] === "pages" && i + 1 < parts.length) {
        //       currentPageID = parts[i + 1];
        //     }
        //   }

        //   if (currentFlowID && currentPageID) {
        //     const flowData = pageData[currentFlowID] || "";
        //     if (flowData) {
        //       if (flowData["skip"].includes(currentPageID)) {
        //         autoDetectLanguageRef.current = false;
        //       }
        //     } else {
        //       autoDetectLanguageRef.current = false;
        //     }
        //   }
        // }
      }
      sendPendingAsk();
    },
    [renderTicket, sendPendingAsk]
  );

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    pendingAskRef.current = consumePendingCampaign();
    const messenger = document.querySelector("df-messenger");
    const bubble = document.querySelector("df-messenger-chat-bubble");

    if (!messenger || !bubble) return;

    dfMessengerRef.current = messenger;
    bubble.openChat();

    const timeoutId = initializeChat(messenger);

    return () => {
      clearTimeout(timeoutId);
      isInitializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const eventHandlers = {
      "df-request-sent": handleSentRequest,
      "df-user-input-entered": clearQueryParameters,
      "df-chip-clicked": clearQueryParameters,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
      "df-genai-clear-chat-session": handleChatSession,
      "df-response-received": handleResponse,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [handleSentRequest, clearQueryParameters, handleSessionExpired, handleClickButton, handleChatSession, handleResponse]);

  if (!isValid) {
    console.error("AIChat configuration is invalid:", error);
    return null;
  }

  return (
    <df-messenger project-id={values.DIALOGFLOW_CX_PROJECT_ID} agent-id={values.DIALOGFLOW_CX_AGENT_ID} allow-feedback="all" location="global">
      <df-messenger-chat-bubble
        chat-title="Sandee"
        allow-fullscreen="always"
        placeholder-text={getText("genai.placeholdertext")}
        chat-collapse-icon={collapse}
      />
    </df-messenger>
  );
}
